import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { getCaseProgress } from "@/lib/caseProgress";
import Header from "@/components/app/Header";
import ConveyancingTimeline from "@/components/app/ConveyancingTimeline";
import QuestionnaireCard from "@/components/client-portal/QuestionnaireCard";
import { House, MapPin, ArrowRight, Clock, FolderSimple, Phone, EnvelopeSimple, MapTrifold, CheckCircle } from "@phosphor-icons/react";

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCases = useCallback(async () => {
    try {
      const { data } = await API.get("/cases");
      setCases(data);
    } catch (err) {
      console.error("Failed to fetch cases", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const getProgress = (stages) => getCaseProgress(stages);

  const getCurrentStage = (stages) => {
    const current = stages?.find((s) => s.status === "current");
    return current?.label || "Not started";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-dashboard">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1
            className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Your Property Transactions
          </h1>
          <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Track the progress of your conveyancing process
          </p>
        </div>

        {cases.length === 0 ? (
          <div
            className="text-center py-16 bg-white border border-slate-200 rounded-lg"
            data-testid="no-cases-client"
          >
            <FolderSimple weight="thin" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No active transactions
            </h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Your solicitor will add you to a case once your property transaction begins.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {cases.map((c) => (
              <div
                key={c.id}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
                data-testid={`client-case-${c.id}`}
              >
                {/* Hero section */}
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1710883734889-5a0b8ab6bfcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBVSyUyMGhvdXNlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc2MTM3NDg0fDA&ixlib=rb-4.1.0&q=85"
                    alt="Property"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <span
                          className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full mb-2 ${
                            c.case_type === "buying"
                              ? "bg-blue-500/90 text-white"
                              : "bg-amber-500/90 text-white"
                          }`}
                        >
                          <House weight="fill" className="w-3 h-3 mr-1" />
                          {c.case_type === "buying" ? "Buying" : "Selling"}
                        </span>
                        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {c.title}
                        </h2>
                        <p className="text-sm text-slate-300 flex items-center gap-1 mt-0.5">
                          <MapPin weight="fill" className="w-3.5 h-3.5" />
                          {c.property_address}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {getProgress(c.stages)}%
                        </span>
                        <p className="text-xs text-slate-300">complete</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Onboarding CTA — only for cases the solicitor has
                    actually enrolled in the onboarding wizard, and only
                    until the client finishes it. Legacy / pre-wizard cases
                    (no onboarding_wizard.enabled) never show this, so
                    existing clients aren't nagged to redo onboarding. */}
                {c.onboarding_wizard?.enabled && !(c.onboarding_wizard?.ta?.status === "completed") && (
                  <div className="border-y border-amber-200 bg-amber-50 px-6 py-3 flex items-center justify-between gap-3" data-testid={`onboarding-cta-${c.id}`}>
                    <div className="text-sm text-amber-900">
                      <p className="font-semibold">Your onboarding isn't finished yet</p>
                      <p className="text-xs text-amber-700 mt-0.5">Please complete payment, identity check, sign the client care letter and finish your property forms.</p>
                    </div>
                    <button
                      onClick={() => navigate(`/client/onboarding/${c.id}`)}
                      data-testid={`onboarding-cta-btn-${c.id}`}
                      className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold bg-[#100f5d] text-white hover:opacity-90 transition-opacity"
                    >
                      Continue onboarding
                    </button>
                  </div>
                )}

                {/* Onboarding done — reassure the client there's nothing
                    left to do, in the same slot the amber CTA used to sit. */}
                {c.onboarding_wizard?.enabled && c.onboarding_wizard?.ta?.status === "completed" && (
                  <div className="border-y border-emerald-200 bg-emerald-50 px-6 py-3 flex items-center gap-3" data-testid={`onboarding-complete-${c.id}`}>
                    <CheckCircle weight="fill" className="w-6 h-6 text-emerald-600 shrink-0" />
                    <div className="text-sm text-emerald-900">
                      <p className="font-semibold">Onboarding complete — thank you</p>
                      <p className="text-xs text-emerald-700 mt-0.5">You've finished everything we need to get started. There's nothing more to do here — we'll keep you updated on your transaction's progress below.</p>
                    </div>
                  </div>
                )}

                {/* Co-seller signature status — only when there are two or
                    more sellers and the property forms aren't fully signed,
                    so joint sellers can see exactly whose signature is still
                    outstanding. */}
                {c.onboarding_ta?.enabled && (c.onboarding_ta?.signers?.length > 1) && !c.onboarding_ta?.complete && (
                  <div className="border-b border-slate-200 bg-slate-50 px-6 py-3" data-testid={`co-seller-status-${c.id}`}>
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                      Property forms — signature status
                    </p>
                    <div className="space-y-1.5">
                      {c.onboarding_ta.signers.map((s) => (
                        <div key={s.client_id} className="flex items-center gap-2 text-sm" data-testid={`co-seller-signer-${c.id}-${s.client_id}`}>
                          {s.signed ? (
                            <CheckCircle weight="fill" className="w-4 h-4 text-emerald-600 shrink-0" />
                          ) : (
                            <Clock weight="fill" className={`w-4 h-4 shrink-0 ${s.stale ? "text-amber-500" : "text-slate-400"}`} />
                          )}
                          <span className="text-slate-800">{s.name}</span>
                          <span className={`text-xs font-medium ${s.signed ? "text-emerald-700" : s.stale ? "text-amber-700" : "text-slate-500"}`}>
                            {s.signed ? "Signed" : s.stale ? "Needs to re-sign" : "Awaiting signature"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
                  {/* Timeline */}
                  <div className="lg:col-span-8">
                    <div className="flex items-center justify-between mb-5">
                      <h3
                        className="text-lg font-semibold text-slate-900"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        Conveyancing Progress
                      </h3>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock weight="bold" className="w-3 h-3" />
                        {getCurrentStage(c.stages)}
                      </span>
                    </div>
                    <ConveyancingTimeline stages={c.stages} isSolicitor={false} />
                  </div>

                  {/* Side info */}
                  <div className="lg:col-span-4 space-y-4">
                    {/* Solicitor Contact */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid={`solicitor-contact-${c.id}`}>
                      <h4 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Your Solicitor
                      </h4>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center shrink-0">
                          <span className="text-white text-sm font-bold">{c.solicitor_name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{c.solicitor_name}</p>
                          <p className="text-[10px] text-blue-600 font-medium">Solicitor</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-xs">
                        {c.solicitor_email && (
                          <a href={`mailto:${c.solicitor_email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors" data-testid={`solicitor-email-${c.id}`}>
                            <EnvelopeSimple weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">{c.solicitor_email}</span>
                          </a>
                        )}
                        {c.solicitor_phone && (
                          <a href={`tel:${c.solicitor_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors" data-testid={`solicitor-phone-${c.id}`}>
                            <Phone weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{c.solicitor_phone}</span>
                          </a>
                        )}
                        {c.solicitor_office && (
                          <div className="flex items-center gap-2 text-slate-600" data-testid={`solicitor-office-${c.id}`}>
                            <MapTrifold weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{c.solicitor_office}</span>
                          </div>
                        )}
                        {!c.solicitor_email && !c.solicitor_phone && !c.solicitor_office && (
                          <p className="text-slate-400 italic">No contact details available yet</p>
                        )}
                      </div>
                    </div>

                    {/* Case Info */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Case Details
                      </h4>
                      <dl className="space-y-2.5">
                        <div>
                          <dt className="text-xs text-slate-500 font-medium">Status</dt>
                          <dd>
                            <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${
                              c.status === "active" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                            }`}>
                              {c.status}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-slate-500 font-medium">Started</dt>
                          <dd className="text-sm text-slate-700">
                            {new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <button
                      onClick={() => navigate(`/cases/${c.id}`)}
                      data-testid={`view-case-detail-${c.id}`}
                      className="w-full flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors group"
                    >
                      <span className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        View documents, tasks & notes
                      </span>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                    </button>
                  </div>
                </div>

                {/* Questionnaires */}
                <div className="px-6 pb-6">
                  <QuestionnaireCard caseId={c.id} viewerRole="client" />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
